import * as THREE from "three";
import gsap from "gsap";

import projectCardVertexShader from "../shaders/projectCard.vert?raw";
import projectCardFragmentShader from "../shaders/projectCard.frag?raw";

import { projects } from "../data/projects.js";
import { SCENE_STATES } from "../data/sceneStates.js";

const TEXTURE_BY_SLUG = {
  bvv: "project-bvv",
  inpott: "project-inpott",
  landingpage: "project-landingpage",
  smarthome: "project-smarthome"
};

export class ProjectCluster {
  constructor({ resources, profile }) {
    this.resources = resources;
    this.profile = profile;
    this.group = new THREE.Group();
    this.cards = [];

    const columns = 2;
    const cardWidth = profile.isMobile ? 1.26 : 1.52;
    const cardHeight = profile.isMobile ? 0.94 : 1.08;
    const spacingX = profile.isMobile ? 1.52 : 1.94;
    const spacingY = profile.isMobile ? 1.12 : 1.32;

    projects.forEach((project, index) => {
      const row = Math.floor(index / columns);
      const column = index % columns;
      const x = (column - 0.5) * spacingX + 1.0;
      const y = (0.5 - row) * spacingY;

      const geometry = new THREE.PlaneGeometry(cardWidth, cardHeight, profile.geometryDetail, profile.geometryDetail);
      const material = new THREE.ShaderMaterial({
        vertexShader: projectCardVertexShader,
        fragmentShader: projectCardFragmentShader,
        uniforms: {
          uTime: { value: 0 },
          uHover: { value: 0.0 },
          uDistortion: { value: 0.6 },
          uTexture: { value: resources.get(TEXTURE_BY_SLUG[project.slug]) }
        },
        transparent: true,
        side: THREE.DoubleSide
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(x, y, -0.95);
      mesh.rotation.y = 0.2 * (column === 0 ? 1 : -1);
      mesh.userData.slug = project.slug;
      mesh.userData.baseY = y;

      this.group.add(mesh);
      this.cards.push(mesh);
    });

    this.group.position.x = 0.95;
    this.group.position.y = -0.18;
    this.group.visible = true;
  }

  transitionTo(state) {
    if (state === SCENE_STATES.PROJECTS || state === SCENE_STATES.PROJECT_DETAIL) {
      gsap.to(this.group.position, { x: 0.2, y: -0.1, duration: 0.8, ease: "power2.out" });
      gsap.to(this.group.rotation, { y: 0, duration: 0.8, ease: "power2.out" });
      gsap.to(this.group.scale, { x: 1, y: 1, z: 1, duration: 0.7, ease: "power2.out" });
      return;
    }

    gsap.to(this.group.position, { x: 1.1, y: -0.28, duration: 0.8, ease: "power2.out" });
    gsap.to(this.group.rotation, { y: -0.25, duration: 0.8, ease: "power2.out" });
    gsap.to(this.group.scale, { x: 0.82, y: 0.82, z: 0.82, duration: 0.7, ease: "power2.out" });
  }

  highlight(slug) {
    this.cards.forEach((mesh) => {
      const isActive = mesh.userData.slug === slug;
      gsap.to(mesh.material.uniforms.uHover, {
        value: isActive ? 1.0 : 0.0,
        duration: 0.45,
        ease: "power2.out"
      });

      gsap.to(mesh.position, {
        z: isActive ? -0.35 : -0.95,
        duration: 0.55,
        ease: "power2.out"
      });
    });
  }

  clearHighlight() {
    this.highlight(null);
  }

  update(elapsed) {
    this.cards.forEach((mesh, index) => {
      mesh.material.uniforms.uTime.value = elapsed + index * 0.22;
      mesh.position.y = mesh.userData.baseY + Math.sin(elapsed * 0.8 + index) * 0.03;
    });
  }

  dispose() {
    this.cards.forEach((mesh) => {
      mesh.geometry.dispose();
      mesh.material.dispose();
    });
  }
}
